// Instructions component for the Buddha Game.
// Shows a subtle, unobtrusive instruction for the player.
// Can be configured to fade out after a number of breathing cycles.

import { useEffect, useState } from 'react';

// ============================================================================
// CONFIGURATION
// ============================================================================

const INSTRUCTIONS_CONFIG = {
  // The instruction text
  text: 'Press and hold Spacebar (or touch/hold / click & hold) to inhale; release to exhale.',

  // Number of cycles before the instruction fades out (0 = never fade)
  fadeAfterCycles: 0, // Set to 0 to keep visible, will be configurable later

  // Fade transition duration in ms
  fadeDuration: 1000,
};

// ============================================================================
// TYPES
// ============================================================================

interface InstructionsProps {
  /** Number of completed breathing cycles */
  cyclesCompleted?: number;
  /** Whether the breathing system is active */
  isActive: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function Instructions({
  cyclesCompleted = 0,
  isActive,
}: InstructionsProps) {
  const [isVisible, setIsVisible] = useState(true);

  // Handle auto-fade after X cycles
  useEffect(() => {
    if (INSTRUCTIONS_CONFIG.fadeAfterCycles > 0) {
      if (cyclesCompleted >= INSTRUCTIONS_CONFIG.fadeAfterCycles) {
        setIsVisible(false);
      }
    }
  }, [cyclesCompleted]);

  // Don't render if not visible
  if (!isVisible) return null;

  return (
    <div
      className="flex items-center justify-center px-4 text-center"
      style={{
        opacity: isActive ? 0.9 : 0.6,
        transition: `opacity ${INSTRUCTIONS_CONFIG.fadeDuration}ms ease`,
      }}
    >
      <p
        className="rounded-lg px-4 py-2 text-sm font-medium"
        style={{
          color: 'rgba(255, 255, 255, 0.95)',
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
          letterSpacing: '0.03em',
          maxWidth: 320,
          backdropFilter: 'blur(4px)',
        }}
      >
        {INSTRUCTIONS_CONFIG.text}
      </p>
    </div>
  );
}
